import { useEffect, useRef } from 'react';
import { useSelectionStore } from './useSelection.js';

/**
 * Convert column name (A, B, ..., Z, AA, AB, ...) to number (1, 2, ..., 26, 27, 28, ...).
 */
function colNameToNum( name ) {
	let num = 0;
	for ( let i = 0; i < name.length; i++ ) {
		num = num * 26 + ( name.charCodeAt( i ) - 64 );
	}
	return num;
}

/**
 * Convert column number (1, 2, ..., 26, 27, ...) to name (A, B, ..., Z, AA, AB, ...).
 */
function numToColName( num ) {
	let name = '';
	while ( num > 0 ) {
		num--;
		name = String.fromCharCode( 65 + ( num % 26 ) ) + name;
		num = Math.floor( num / 26 );
	}
	return name;
}

/**
 * Parse tile ID (e.g., "P39", "AA34") into column number and row.
 */
function parseTileId( id ) {
	const match = id.match( /^([A-Z]+)(\d+)$/ );
	if ( ! match ) return null;
	return { colNum: colNameToNum( match[1] ), row: parseInt( match[2] ) };
}

/**
 * Convert col number/row back to tile ID.
 */
function tileIdFromCoords( colNum, row ) {
	return `${ numToColName( colNum ) }${ row }`;
}

/**
 * Generate range string for contiguous tiles (e.g., "P-Q39-41", "AA-AB34-36").
 */
function generateRangeString( tiles ) {
	if ( tiles.length === 0 ) return null;
	if ( tiles.length === 1 ) return tiles[0];

	// Parse all tiles
	const parsed = tiles.map( parseTileId ).filter( Boolean );
	if ( parsed.length === 0 ) return null;

	const colNums   = [...new Set( parsed.map( ( p ) => p.colNum ) )].sort( ( a, b ) => a - b );
	const rows      = [...new Set( parsed.map( ( p ) => p.row ) )].sort( ( a, b ) => a - b );
	const minRow    = rows[0];
	const maxRow    = rows[rows.length - 1];

	// Check if it's a valid rectangle: all (colNum, row) combinations exist
	const hasAllCombos = colNums.every( ( c ) => {
		for ( let r = minRow; r <= maxRow; r++ ) {
			if ( ! tiles.includes( tileIdFromCoords( c, r ) ) ) return false;
		}
		return true;
	} );

	if ( ! hasAllCombos ) return null;

	// Generate range: "P-Q39-41" (col range, row range)
	const minColNum = colNums[0];
	const maxColNum = colNums[colNums.length - 1];

	if ( minColNum === maxColNum ) {
		// Single column: "P39-41"
		if ( minRow === maxRow ) return tileIdFromCoords( minColNum, minRow );
		return `${ numToColName( minColNum ) }${ minRow }-${ maxRow }`;
	}

	// Multiple columns: "P-Q39-41"
	return `${ numToColName( minColNum ) }-${ numToColName( maxColNum ) }${ minRow }-${ maxRow }`;
}

/**
 * Converts internal selection (individual tiles) to Variant-A format:
 * complete bundles become "#<bundleId>"; contiguous tiles get range format (e.g., "P-Q39-41");
 * remaining individual tiles keep their ID.
 */
function toVariantA( selected, normalizedBundles = [] ) {
	const result   = [];
	const consumed = new Set();

	// First: match complete bundles
	for ( const bundle of normalizedBundles ) {
		if ( bundle.tile_ids.length === 0 ) continue;
		if ( bundle.tile_ids.every( ( id ) => !! selected[ id ] ) ) {
			result.push( `#${ bundle.id }` );
			bundle.tile_ids.forEach( ( id ) => consumed.add( id ) );
		}
	}

	// Remaining tiles to process
	const remaining = Object.keys( selected ).filter( ( id ) => ! consumed.has( id ) );

	if ( remaining.length === 0 ) return result;

	// Build a set for fast lookup
	const remainingSet = new Set( remaining );
	const used         = new Set();

	// Process tiles in column-major order (optimized)
	const sorted = [...remaining].sort( ( a, b ) => {
		const pa = parseTileId( a );
		const pb = parseTileId( b );
		if ( ! pa || ! pb ) return 0;
		if ( pa.colNum !== pb.colNum ) return pa.colNum - pb.colNum;
		return pa.row - pb.row;
	} );

	// Build a map of what's available for faster lookup
	const availMap = new Map();
	for ( const id of remaining ) {
		const parsed = parseTileId( id );
		if ( parsed ) {
			const key = `${ parsed.colNum },${ parsed.row }`;
			availMap.set( key, id );
		}
	}

	for ( const startId of sorted ) {
		if ( used.has( startId ) ) continue;

		const start = parseTileId( startId );
		if ( ! start ) {
			result.push( startId );
			used.add( startId );
			continue;
		}

		// Find largest rectangle starting from this tile (greedy)
		let maxCols = 1;
		let maxRows = 1;

		// Expand to the right (max 200 cols to prevent runaway)
		for ( let c = start.colNum + 1; c < start.colNum + 200; c++ ) {
			const key = `${ c },${ start.row }`;
			if ( availMap.has( key ) && ! used.has( availMap.get( key ) ) ) {
				maxCols++;
			} else {
				break;
			}
		}

		// Expand downward with current width (max 1000 rows)
		for ( let r = start.row + 1; r < start.row + 1000; r++ ) {
			let rowValid = true;
			for ( let c = 0; c < maxCols; c++ ) {
				const key = `${ start.colNum + c },${ r }`;
				if ( ! availMap.has( key ) || used.has( availMap.get( key ) ) ) {
					rowValid = false;
					break;
				}
			}
			if ( rowValid ) {
				maxRows++;
			} else {
				break;
			}
		}

		// Collect all tiles in this rectangle
		const rangeTiles = [];
		for ( let c = 0; c < maxCols; c++ ) {
			for ( let r = 0; r < maxRows; r++ ) {
				const colNum = start.colNum + c;
				const id     = tileIdFromCoords( colNum, start.row + r );
				rangeTiles.push( id );
				used.add( id );
			}
		}

		// Generate range string
		const rangeStr = generateRangeString( rangeTiles );
		result.push( rangeStr || startId );
	}

	return result;
}

/**
 * Convert Variant-A format back to individual tile IDs.
 * Bundles that can't be expanded are preserved as bundle IDs (prefixed with #).
 */
function fromVariantA( variantAIds, normalizedBundles = [] ) {
	const tileIds = [];

	for ( const id of variantAIds ) {
		if ( id.startsWith( '#' ) ) {
			// Bundle ID: try to expand, or keep as-is if not found
			const bundleId = id.slice( 1 );
			const bundle   = normalizedBundles.find( ( b ) => b.id === bundleId );
			if ( bundle ) {
				tileIds.push( ...bundle.tile_ids );
			} else {
				// Bundle not found in normalizedBundles — keep the bundle ID as-is
				// This preserves bundles that might exist in the DB but can't be expanded yet
				tileIds.push( id );
			}
		} else if ( id.includes( '-' ) ) {
			// Range format: "P-Q39-41" or "P39-41"
			const rangeMatch = id.match( /^([A-Z]+)(?:-([A-Z]+))?(\d+)-(\d+)$/ );
			if ( rangeMatch ) {
				const startCol = rangeMatch[1];
				const endCol   = rangeMatch[2] || startCol;
				const startRow = parseInt( rangeMatch[3] );
				const endRow   = parseInt( rangeMatch[4] );

				const startColNum = colNameToNum( startCol );
				const endColNum   = colNameToNum( endCol );

				for ( let c = startColNum; c <= endColNum; c++ ) {
					for ( let r = startRow; r <= endRow; r++ ) {
						tileIds.push( tileIdFromCoords( c, r ) );
					}
				}
			} else {
				// Single row range: "P39-41"
				const rowRangeMatch = id.match( /^([A-Z]+)(\d+)-(\d+)$/ );
				if ( rowRangeMatch ) {
					const col  = rowRangeMatch[1];
					const from = parseInt( rowRangeMatch[2] );
					const to   = parseInt( rowRangeMatch[3] );

					for ( let r = from; r <= to; r++ ) {
						tileIds.push( `${ col }${ r }` );
					}
				} else {
					tileIds.push( id );
				}
			}
		} else {
			// Individual tile ID
			tileIds.push( id );
		}
	}

	return tileIds;
}

/**
 * Hook for bidirectional sync between Zustand store and HTML input element.
 * Initializes store from input element and syncs in both directions.
 * Input value uses Variant-A format (bundles as "#id", ranges as "P-Q39-41", tiles as tile IDs).
 *
 * @param {string} fieldType - Field type identifier (e.g., 'tartan', 'normal')
 * @param {Array} normalizedBundles - Bundle definitions for Variant-A conversion
 */
export function useInputState( fieldType, normalizedBundles = [], isLoading = false ) {
	const isSyncingRef = useRef( false );

	useEffect( () => {
		if ( isLoading || ! fieldType ) return;

		const inputEl = document.querySelector(
			`.wsp-tiles-input[data-field-type="${ fieldType }"]`
		);
		if ( ! inputEl ) return;

		// Initialize store from input element value
		try {
			const value = inputEl.value?.trim();
			let variantAIds = [];
			if ( value ) {
				variantAIds = JSON.parse( value );
				if ( ! Array.isArray( variantAIds ) ) variantAIds = [];
			}
			const tileIds = fromVariantA( variantAIds, normalizedBundles );
			useSelectionStore.getState().selectMany( fieldType, tileIds );
		} catch ( e ) {
			console.error( `Failed to initialize tiles for "${ fieldType }"`, e );
		}

		// Listen for manual input changes (user edits the input directly)
		const handleInputChange = () => {
			if ( isSyncingRef.current ) return;
			try {
				const value = inputEl.value?.trim();
				let variantAIds = [];
				if ( value ) {
					variantAIds = JSON.parse( value );
					if ( ! Array.isArray( variantAIds ) ) variantAIds = [];
				}
				const tileIds = fromVariantA( variantAIds, normalizedBundles );
				const store = useSelectionStore.getState();
				store.clear( fieldType );
				store.selectMany( fieldType, tileIds );
			} catch ( e ) {
				console.warn( `Invalid tiles format for "${ fieldType }"`, e );
			}
		};
		inputEl.addEventListener( 'change', handleInputChange );

		// Extract formId from data-gf-field-id (format: "1_14" → formId=1)
		const gfFieldId = inputEl.dataset.gfFieldId || '';
		const [ formIdStr ] = gfFieldId.split( '_' );
		const formId = parseInt( formIdStr, 10 );

		// Sync store changes → input element
		const unsubscribe = useSelectionStore.subscribe( ( state ) => {
			const selected = state.selected[ fieldType ];
			if ( ! selected ) return;
			try {
				isSyncingRef.current = true;
				inputEl.value = JSON.stringify( toVariantA( selected, normalizedBundles ) );
				inputEl.dispatchEvent( new Event( 'change', { bubbles: true } ) );
				// Notify GF's price calculations — must include formId or it's a no-op
				if ( window.gformCalculateTotalPrice && formId ) {
					window.gformCalculateTotalPrice( formId );
				}
			} catch ( e ) {
				console.error( `Failed to sync tiles to input for "${ fieldType }"`, e );
			} finally {
				isSyncingRef.current = false;
			}
		} );

		return () => {
			inputEl.removeEventListener( 'change', handleInputChange );
			unsubscribe();
		};
	}, [ fieldType, normalizedBundles, isLoading ] );
}
